import type { EstimatorInput, ProjectType, ScopeLevel, FinishLevel, YesNoUnknown } from "@reno-estimator/core";

/**
 * Raw answer bag as collected by the question flow, keyed by question id
 * (see @reno-estimator/config). Pure data shuffling only - this file must
 * never contain pricing or business logic, only shape translation.
 */
export interface RawAnswerBag {
  [questionId: string]: unknown;
}

function asYesNoUnknown(value: unknown): YesNoUnknown {
  return value === "yes" || value === "no" ? value : "unknown";
}

export function mapAnswersToEstimatorInput(params: {
  requestId: string;
  projectType: ProjectType;
  answers: RawAnswerBag;
  customProjectDescription?: string;
}): EstimatorInput {
  const { requestId, projectType, answers, customProjectDescription } = params;

  return {
    inputSchemaVersion: "0.1.0",
    requestId,
    submittedAt: new Date().toISOString(),
    projectType,
    customProjectDescription,
    roomSize: answers.room_size as EstimatorInput["roomSize"],
    location: (answers.location as EstimatorInput["location"]) ?? { province: "ON" },
    scope: (answers.scope as ScopeLevel) ?? "mid_range_remodel",
    finishLevel: (answers.finish_level as FinishLevel) ?? "standard",
    materials: [],
    fixturesAndAppliances: [],
    workType: {
      plumbingChanges: asYesNoUnknown(answers.plumbing_changes),
      electricalChanges: asYesNoUnknown(answers.electrical_changes),
      structuralChanges: asYesNoUnknown(answers.structural_changes),
      demolitionRequired: asYesNoUnknown(answers.demolition_required)
    },
    designAndPermits: {
      needsDesignServices: answers.needs_design_services ? "yes" : "no",
      permitLikelyRequired: asYesNoUnknown(answers.permit_likely_required)
    },
    accessibility: {
      required: asYesNoUnknown(answers.accessibility_required),
      notes: answers.accessibility_notes as string | undefined
    },
    timeline: {
      desiredStartWindow: answers.timeline_window as EstimatorInput["timeline"]["desiredStartWindow"],
      flexible: answers.timeline_window === "flexible" || answers.timeline_window === undefined
    },
    rawAnswers: answers
  };
}
